(function () {
  'use strict';

  const CONFIG_SELECTOR = '#EcomhardFreeGiftConfig';
  const GIFT_PROPERTY_NAME = '_ecomhard_free_gift';
  const GIFT_PROPERTY_VALUE = 'true';
  const DISMISSED_SIGNATURE_KEY = 'ecomhard_free_gift_dismissed_signature';

  let syncTimer = null;
  let isSyncing = false;

  function readConfig() {
    const script = document.querySelector(CONFIG_SELECTOR);
    if (!script) return null;

    try {
      const config = JSON.parse(script.textContent || '{}');
      config.enabled = Boolean(config.enabled);
      config.giftVariantId = Number(config.giftVariantId || 0);
      config.giftProductId = Number(config.giftProductId || 0);
      config.triggerProductIds = Array.isArray(config.triggerProductIds)
        ? config.triggerProductIds.map(Number).filter(Boolean)
        : [];
      config.productRule = config.productRule || 'any';
      config.anyMinProducts = Math.max(1, Number(config.anyMinProducts || 1));
      config.cartTotalEnabled = Boolean(config.cartTotalEnabled);
      config.cartTotalThresholdCents = parseMoneyToCents(config.cartTotalThreshold || 0);
      config.validationMode = config.validationMode || 'products_only';
      config.giftPropertyName = config.giftPropertyName || GIFT_PROPERTY_NAME;
      config.giftPropertyValue = config.giftPropertyValue || GIFT_PROPERTY_VALUE;
      return config;
    } catch (error) {
      console.warn('[ECOMHARD Free Gift] Configuração inválida.', error);
      return null;
    }
  }

  function parseMoneyToCents(value) {
    if (typeof value === 'number') return Math.round(value * 100);

    const normalized = String(value || '')
      .replace(/[^\d.,]/g, '')
      .trim();

    if (!normalized) return 0;

    let amount = normalized;

    if (amount.includes(',') && amount.includes('.')) {
      amount = amount.replace(/\./g, '').replace(',', '.');
    } else if (amount.includes(',')) {
      amount = amount.replace(',', '.');
    }

    const parsed = Number.parseFloat(amount);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }

  function getRoutes() {
    const routes = window.routes || {};
    return {
      cart: routes.cart_url ? `${routes.cart_url}.js` : '/cart.js',
      add: routes.cart_add_url || '/cart/add.js',
      change: routes.cart_change_url || '/cart/change.js'
    };
  }

  function fetchConfig() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    };
  }

  async function getCart() {
    const response = await fetch(getRoutes().cart, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Não foi possível buscar o carrinho.');
    return response.json();
  }

  function isFreeGiftItem(item, config) {
    if (!item || !item.properties) return false;
    return String(item.properties[config.giftPropertyName] || '') === config.giftPropertyValue;
  }

  function getGiftItems(cart, config) {
    return (cart.items || []).filter((item) => Number(item.id) === config.giftVariantId && isFreeGiftItem(item, config));
  }

  function getPaidItems(cart, config) {
    return (cart.items || []).filter((item) => !isFreeGiftItem(item, config));
  }

  function getCartSignature(cart, config) {
    return getPaidItems(cart, config)
      .map((item) => `${item.product_id}:${item.id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  function isGiftDismissed(cart, config) {
    try {
      return sessionStorage.getItem(DISMISSED_SIGNATURE_KEY) === getCartSignature(cart, config);
    } catch (error) {
      return false;
    }
  }

  function dismissGiftForCurrentCart(cart, config) {
    try {
      sessionStorage.setItem(DISMISSED_SIGNATURE_KEY, getCartSignature(cart, config));
    } catch (error) {
      // sessionStorage pode estar indisponível em alguns navegadores/modos privados.
    }
  }

  function getMatchedTriggerProductIds(cart, config) {
    const triggerIds = new Set(config.triggerProductIds);
    const matched = new Set();

    getPaidItems(cart, config).forEach((item) => {
      const productId = Number(item.product_id);
      if (triggerIds.has(productId)) matched.add(productId);
    });

    return [...matched];
  }

  function isProductsRuleValid(cart, config) {
    if (!config.triggerProductIds.length) return false;

    const matched = getMatchedTriggerProductIds(cart, config);

    if (config.productRule === 'all') {
      return config.triggerProductIds.every((productId) => matched.includes(productId));
    }

    return matched.length >= config.anyMinProducts;
  }

  function isCartTotalRuleValid(cart, config) {
    if (!config.cartTotalEnabled) return false;

    const subtotalWithoutGift = getPaidItems(cart, config).reduce((total, item) => {
      return total + Number(item.final_line_price || 0);
    }, 0);

    return subtotalWithoutGift >= config.cartTotalThresholdCents;
  }

  function shouldHaveGift(cart, config) {
    const productsValid = isProductsRuleValid(cart, config);
    const totalValid = isCartTotalRuleValid(cart, config);

    switch (config.validationMode) {
      case 'cart_total_only':
        return totalValid;
      case 'products_or_cart_total':
        return productsValid || totalValid;
      case 'products_and_cart_total':
        return productsValid && totalValid;
      case 'products_only':
      default:
        return productsValid;
    }
  }

  function getSectionId() {
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    return drawer ? drawer.dataset.sectionId : null;
  }

  function buildSectionPayload() {
    const sectionId = getSectionId();

    if (!sectionId) return {};

    return {
      sections: sectionId,
      sections_url: window.location.pathname
    };
  }

  async function addGift(config) {
    const payload = {
      items: [
        {
          id: config.giftVariantId,
          quantity: 1,
          properties: {
            [config.giftPropertyName]: config.giftPropertyValue
          }
        }
      ],
      ...buildSectionPayload()
    };

    const response = await fetch(getRoutes().add, {
      ...fetchConfig(),
      body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error('Não foi possível adicionar o brinde.');
    return response.json();
  }

  async function changeLine(lineKey, quantity) {
    const response = await fetch(getRoutes().change, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: lineKey,
        quantity,
        ...buildSectionPayload()
      })
    });

    if (!response.ok) throw new Error('Não foi possível atualizar o brinde.');
    return response.json();
  }

  function refreshCartDrawer() {
    const drawer = document.querySelector('cart-drawer');

    if (drawer && typeof drawer.update === 'function') {
      drawer.update();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function normalizeGiftLines(cart, config, eligible) {
    const giftItems = getGiftItems(cart, config);

    if (!eligible) {
      for (const item of giftItems) {
        await changeLine(item.key, 0);
      }
      return giftItems.length > 0;
    }

    if (giftItems.length === 0) {
      if (isGiftDismissed(cart, config)) return false;
      await addGift(config);
      return true;
    }

    let changed = false;
    const [mainGift, ...duplicatedGifts] = giftItems;

    if (Number(mainGift.quantity) !== 1) {
      await changeLine(mainGift.key, 1);
      changed = true;
    }

    for (const item of duplicatedGifts) {
      await changeLine(item.key, 0);
      changed = true;
    }

    return changed;
  }

  async function syncFreeGift() {
    const config = readConfig();
    if (!config || !config.enabled || !config.giftVariantId || isSyncing) return;

    isSyncing = true;

    try {
      const cart = await getCart();
      const eligible = shouldHaveGift(cart, config);
      const changed = await normalizeGiftLines(cart, config, eligible);

      if (changed) refreshCartDrawer();
    } catch (error) {
      console.warn('[ECOMHARD Free Gift]', error);
    } finally {
      isSyncing = false;
    }
  }

  function scheduleSync(delay = 350) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFreeGift, delay);
  }

  async function handleGiftRemoveClick(event) {
    const removeButton = event.target.closest('cart-item[data-ecomhard-free-gift="true"] cart-remove-button');
    if (!removeButton) return;

    const config = readConfig();
    if (!config) return;

    try {
      const cart = await getCart();
      dismissGiftForCurrentCart(cart, config);
    } catch (error) {
      // Se não conseguir gravar a remoção manual, segue o fluxo padrão do tema.
    }
  }

  function watchCartActions() {
    document.addEventListener('click', handleGiftRemoveClick, true);

    document.addEventListener(
      'change',
      (event) => {
        if (event.target.closest('cart-drawer, cart-page, form[action*="/cart"]')) {
          scheduleSync(900);
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (event.target.matches('form[action*="/cart/add"]')) {
          scheduleSync(900);
        }
      },
      true
    );
  }

  function patchFetch() {
    if (window.__ecomhardFreeGiftFetchPatched) return;
    window.__ecomhardFreeGiftFetchPatched = true;

    const nativeFetch = window.fetch;

    window.fetch = function patchedFetch(input, init) {
      const requestUrl = typeof input === 'string' ? input : input && input.url ? input.url : '';
      const responsePromise = nativeFetch.apply(this, arguments);

      if (!isSyncing && /\/cart\/(add|change|update|clear)(\.js)?/.test(requestUrl)) {
        responsePromise
          .then(() => scheduleSync(700))
          .catch(() => {});
      }

      return responsePromise;
    };
  }

  function init() {
    const config = readConfig();
    if (!config || !config.enabled) return;

    patchFetch();
    watchCartActions();
    scheduleSync(200);

    window.addEventListener('pageshow', () => scheduleSync(200));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
